import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface ConsentInfoSheetProps {
  open: boolean;
  onClose: () => void;
}
const SECTIONS = [{
  heading: 'Your AI identifies itself',
  body: "When a call is forwarded to Vexu, your AI assistant answers and lets the caller know they're speaking with an AI, not you directly. It's built to help manage your calls — not for emergencies. Vexu is not an emergency service, and your AI cannot recognize or handle emergency situations. Do not rely on Vexu for calls where an emergency response may be needed."
}, {
  heading: 'Calls may be recorded or transcribed',
  body: "Depending on your location, calls handled by your AI may be recorded and transcribed so you can review them later. Where the law requires it, callers will be notified before recording begins. You'll have access to summaries, transcripts, and recordings (where enabled) for every call in your call history. You're responsible for using recording features in line with the laws that apply to you and your callers."
}, {
  heading: "You're authorizing call forwarding",
  body: "By continuing, you're authorizing your mobile carrier to forward calls matching the conditions you choose (busy, unanswered, unreachable, or all calls) to your Vexu AI assistant. Your carrier may charge standard rates for forwarded calls — check your plan for details. You can turn forwarding off anytime; Vexu will guide you through deactivating it with your carrier."
}, {
  heading: 'If your service ends',
  body: 'If your subscription is cancelled, expires, or is paused due to a failed payment, your AI will stop answering forwarded calls. Callers may hear an automated message saying the service is unavailable, or the call may not connect. Call forwarding is set on your phone line with your carrier — it does not turn off automatically. Remember to disable forwarding (the app shows you how) if you stop using Vexu.'
}];
export function ConsentInfoSheet({
  open,
  onClose
}: ConsentInfoSheetProps) {
  const {
    isLight
  } = useTheme();
  return <AnimatePresence data-id="element-1059" data-mp="2kyazsx2ekl1">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 z-40 bg-black/50 backdrop-blur-sm" data-id="element-1060" data-mp="2mramo99g4jj" />

          {/* Sheet */}
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 36
      }} drag="y" dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0,
        bottom: 0.4
      }} onDragEnd={(_, info) => {
        if (info.offset.y > 120) onClose();
      }} role="dialog" aria-modal="true" aria-label="How your AI handles calls" className={`absolute inset-x-0 bottom-0 z-50 rounded-t-3xl max-h-[88%] flex flex-col ${isLight ? 'bg-white' : 'bg-[#1c1c1e]'}`} data-id="element-1061" data-mp="3f3ed3qxh4kqu">
            {/* Drag handle */}
            <div className="pt-2.5 pb-1 flex justify-center flex-shrink-0" data-id="element-1062" data-mp="1liuklbsg57dc">
              <div className={`w-9 h-1 rounded-full ${isLight ? 'bg-slate-300' : 'bg-white/20'}`} data-id="element-1063" data-mp="16tyj5xkwaslp" />
            </div>

            {/* Header */}
            <div className="px-5 pt-3 flex items-start justify-between gap-3 flex-shrink-0" data-id="element-1064" data-mp="1zj5eyosyt439">
              <h2 className={`text-[18px] font-bold leading-snug ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1065" data-mp="24anhhhamzcqc">
                How your AI handles calls
              </h2>
              <button onClick={onClose} aria-label="Close" className={`p-1.5 -mr-1 rounded-full transition-colors flex-shrink-0 ${isLight ? 'text-slate-500 hover:bg-slate-100' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-1066" data-mp="bmg0d9t2tesq">
                <X className="w-5 h-5" data-id="element-1067" data-mp="zc0lnzcpco56" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-5 pt-5" style={{
          paddingBottom: 32
        }} data-id="element-1068" data-mp="ucxd44xijzsg">
              {SECTIONS.map((section, i) => <div key={section.heading} data-id="element-1069" data-mp="kmcj6ussbjfv" data-mpi={i}>
                  {i > 0 && <div className={`my-4 border-t ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-1070" data-mp="1i2i1x4gclr9n" />}
                  <h3 className={`text-[15px] font-bold mb-1.5 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1071" data-mp="1d9yoc7hnuw4u">
                    {section.heading}
                  </h3>
                  <p className="text-[13px] leading-[1.5]" style={{
              color: isLight ? '#6B7280' : '#a1a1aa'
            }} data-id="element-1072" data-mp="1uldley9dyq3a">
                    {section.body}
                  </p>
                </div>)}

              {/* Footer line */}
              <p className="text-[12px] leading-[1.5] mt-6" style={{
            color: isLight ? '#9CA3AF' : '#71717a'
          }} data-id="element-1073" data-mp="28k3iwf04ars6">
                For full details, see our{' '}
                <button type="button" onClick={() => {
              /* TODO: open Terms of Service */
            }} className={`underline font-medium ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1074" data-mp="2id2h8olf1woz">
                  Terms of Service
                </button>{' '}
                and{' '}
                <button type="button" onClick={() => {
              /* TODO: open Privacy Policy */
            }} className={`underline font-medium ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1075" data-mp="1xn8h0hccdj0t">
                  Privacy Policy
                </button>
                .
              </p>
              <p className={`mt-1 text-center text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1076" data-mp="2gd6kek572ny4">Consent v1.0 — July 2026</p>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}